(function (root) {
  'use strict';

  function stepsFor(answers) {
    const steps = ['owner'];
    if (!answers.owner) return steps;
    if (answers.owner === 'yes') steps.push('brand', 'model', 'supplies');
    else {
      steps.push('print', 'lines');
      if (answers.lines !== '2') steps.push('volume');
      steps.push('extras');
      if (answers.extras !== 'yes') return [...steps, 'results'];
      steps.push('supplies');
    }
    if (answers.supplies !== 'ink') steps.push('style');
    if (!steps.includes('volume')) steps.push('volume');
    return [...steps, 'results'];
  }

  function monthlyProducts(answers) { return Number(answers.productsPerMonth ?? 1000); }

  function labelsForPeriod(answers, months) {
    // Integer-ratio math avoids adding an extra label from 1.10 floating-point rounding.
    return Math.ceil(monthlyProducts(answers) * 11 * months / 10);
  }

  function chooseModel(models, answers) {
    if (answers.owner === 'yes') return models.find(model => model.key === answers.model);
    const lines = Number(answers.lines || 1);
    const volume = labelsForPeriod(answers, 12);
    if (lines === 1
      && ((volume > 50000 && volume <= 150000) || (volume > 300000 && volume <= 750000))) {
      return models.find(model => model.key === 'perco-plus');
    }
    const candidates = models.filter(model => model.brand === 'Perco' && model.lines === lines)
      .sort((a, b) => a.maxAnnual - b.maxAnnual);
    return candidates.find(model => volume <= model.maxAnnual) || candidates.at(-1);
  }

  function packUnits(variant) {
    const option = variant.options.find(value => /\d[\d,.]*\s*(sleeve|roll|pack|case)/i.test(value));
    const match = option?.replace(/,/g, '').match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : 0;
  }

  function variantStyle(product, variant) {
    const indexes = (product.options || []).map((option, index) =>
      !/quantity|pack|size/i.test(option.name) && option.values.some(value => !/default title/i.test(value)) ? index : -1)
      .filter(index => index >= 0);
    const values = product.options?.length ? indexes.map(index => variant.options[index])
      : variant.options.filter(value => !/default title|\d[\d,.]*\s*(sleeve|roll|pack|case)/i.test(value));
    return values.filter(Boolean).join(' / ') || 'White';
  }

  function styleOptions(product) {
    return product ? [...new Set(product.variants.filter(variant => inventoryLimit(variant) > 0 && packUnits(variant) > 0)
      .map(variant => variantStyle(product, variant)))] : [];
  }

  function selectedStyles(style) {
    return [...new Set((Array.isArray(style) ? style : style ? [style] : ['White']).filter(Boolean))];
  }

  function labelVariants(product, style = 'White') {
    if (!product) return [];
    return product.variants.filter(variant => inventoryLimit(variant) > 0 && packUnits(variant) > 0
      && variantStyle(product, variant).toLowerCase() === String(style).toLowerCase());
  }

  function inventoryLimit(variant) {
    if (!variant.available) return 0;
    if (!variant.inventoryTracked) return Infinity;
    return Math.max(0, Number(variant.inventoryQuantity || 0) - Number(variant.cartQuantity || 0));
  }

  function inkUnits(variant) { return Number(variant.baseUnits || 0); }

  function addedCartLines(before, after, lines) {
    const counts = cart => {
      const values = new Map();
      for (const item of cart.items) {
        const key = `${item.variant_id}:${item.selling_plan_allocation?.selling_plan.id || ''}`;
        values.set(key, (values.get(key) || 0) + item.quantity);
      }
      return values;
    };
    const original = counts(before), current = counts(after);
    return lines.map(line => {
      const key = `${line.variant.id}:${line.allocation?.id || ''}`;
      return { line, added: Math.max(0, (current.get(key) || 0) - (original.get(key) || 0)) };
    });
  }

  function colorMix(styles, mix = {}) {
    if (styles.length === 1) return { [styles[0]]: 100 };
    if (styles.length && styles.every(style => Number.isInteger(mix[style]) && mix[style] >= 0)
      && styles.reduce((sum, style) => sum + mix[style], 0) === 100) return Object.fromEntries(styles.map(style => [style, mix[style]]));
    return Object.fromEntries(styles.map((style, index) => [style, Math.floor(100 / styles.length) + (index < 100 % styles.length ? 1 : 0)]));
  }

  function adjustColorMix(styles, mix, changed, value) {
    const next = colorMix(styles, mix);
    if (styles.length < 2) return next;
    next[changed] = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
    const others = styles.filter(style => style !== changed);
    const remainder = 100 - next[changed];
    const weight = others.reduce((sum, style) => sum + next[style], 0);
    let assigned = 0;
    const shares = others.map(style => {
      const share = remainder * (weight ? next[style] / weight : 1 / others.length);
      next[style] = Math.floor(share); assigned += next[style];
      return { style, fraction: share % 1 };
    }).sort((a, b) => b.fraction - a.fraction);
    for (let index = 0; assigned < 100 - next[changed]; index++, assigned++) next[shares[index].style]++;
    return next;
  }

  // Bounded whole-pack optimization: each actual SKU can be used only up to its stock.
  function planPacks(variants, requiredSleeves, planName = '', unitsFor = packUnits) {
    if (requiredSleeves === 0) return { supplied: 0, total: 0, lines: [] };
    const packs = variants.map(variant => {
      const allocation = planName ? variant.plans?.find(plan => plan.name === planName) : null;
      return { variant, units: unitsFor(variant), price: allocation ? allocation.price : variant.price, allocation, stock: inventoryLimit(variant) };
    }).filter(pack => pack.units > 0 && pack.stock > 0 && Number.isInteger(pack.units) && (!planName || pack.allocation));
    if (!packs.length || !Number.isFinite(requiredSleeves) || requiredSleeves < 1) return null;
    const limit = requiredSleeves + Math.max(...packs.map(pack => pack.units)) - 1;
    if (limit > 5000) return null;
    const costs = Array(limit + 1).fill(Infinity);
    const combinations = Array(limit + 1).fill(null);
    costs[0] = 0;
    combinations[0] = [];
    for (const pack of packs) {
      let remaining = Math.min(pack.stock, Math.floor(limit / pack.units));
      for (let chunk = 1; remaining > 0; chunk *= 2) {
        const quantity = Math.min(chunk, remaining);
        remaining -= quantity;
        const units = quantity * pack.units;
        for (let count = limit; count >= units; count--) {
          const cost = costs[count - units] + quantity * pack.price;
          if (cost < costs[count]) {
            costs[count] = cost;
            combinations[count] = [...combinations[count - units], { pack, quantity }];
          }
        }
      }
    }
    let supplied = requiredSleeves;
    for (let count = requiredSleeves; count <= limit; count += 1) {
      if (costs[count] < costs[supplied]) supplied = count;
    }
    if (!Number.isFinite(costs[supplied])) return null;
    const lines = new Map();
    for (const { pack, quantity } of combinations[supplied]) {
      const line = lines.get(pack.variant.id) || { variant: pack.variant, allocation: pack.allocation, quantity: 0, unitPrice: pack.price };
      line.quantity += quantity;
      lines.set(pack.variant.id, line);
    }
    return { supplied, total: costs[supplied], lines: [...lines.values()] };
  }

  function gunVariant(product) {
    return product?.variants.find(variant => inventoryLimit(variant) >= 1 && /(^|\s|\/)white($|\s|\/)/i.test(variant.title))
      || product?.variants.find(variant => inventoryLimit(variant) >= 1);
  }

  function recommendation(models, answers, planName = '') {
    const model = chooseModel(models, answers);
    if (!model) return { error: 'We could not find a compatible model. Our team can help you choose.' };
    const gun = answers.print === 'date' ? model.dateGun : model.priceGun;
    const wantsSupplies = answers.owner === 'yes' || answers.extras === 'yes';
    const wantsLabels = wantsSupplies && answers.supplies !== 'ink';
    const wantsInk = wantsSupplies && answers.supplies !== 'labels' && model.key !== 'monarch-1153' && answers.includeInk !== false;
    const lines = [];
    let labelsNeeded = 0;
    let sleevesNeeded = 0;
    const colorPlans = [];
    const styles = selectedStyles(answers.style);
    const mix = colorMix(styles, answers.colorMix);
    const months = Number(answers.months || 3);
    if (answers.owner === 'no') {
      const variant = gunVariant(gun);
      if (!variant) return { model, error: 'The matched gun is currently unavailable. Contact us for an alternative.' };
      lines.push({ product: gun, variant, quantity: 1, unitPrice: variant.price, kind: 'gun' });
    }
    if (wantsLabels) {
      if (!(model.labelsPerSleeve > 0)) return { model, error: 'Please contact us to confirm the label pack quantity for this model.' };
      labelsNeeded = labelsForPeriod(answers, Number(answers.months || 3));
      sleevesNeeded = Math.ceil(labelsNeeded / model.labelsPerSleeve);
      const availableStyles = styleOptions(model.labels);
      if (!styles.length || styles.some(style => mix[style] > 0 && !availableStyles.includes(style) && !answers.excludedColors?.includes(style))) {
        return { model, error: 'Please choose available label colors compatible with your gun.' };
      }
      for (const style of styles) {
        const needed = Math.ceil(monthlyProducts(answers) * 11 * months * mix[style] / 1000);
        const required = Math.ceil(needed / model.labelsPerSleeve);
        if (answers.excludedColors?.includes(style)) {
          colorPlans.push({ style, percent: mix[style], needed, required, supplied: 0, excluded: true });
          continue;
        }
        const packPlan = planPacks(labelVariants(model.labels, style), required, planName);
        if (!packPlan) return { model, stockIssue: true, error: `There is not enough available ${style} label stock for this ${months}-month supply. Choose a shorter available period, another color, or a lower monthly usage if appropriate.` };
        colorPlans.push({ style, percent: mix[style], needed, required, supplied: packPlan.supplied });
        lines.push(...packPlan.lines.map(line => ({ ...line, product: model.labels, kind: 'labels', style })));
      }
    }
    const includedInkers = lines.filter(line => line.kind === 'labels' && line.variant.inkIncluded)
      .reduce((sum, line) => sum + packUnits(line.variant) * line.quantity, 0);
    const inkersNeeded = Math.ceil(labelsForPeriod(answers, months) / model.labelsPerSleeve);
    const inkChoices = [];
    if (wantsInk) {
      const candidates = (model.ink?.variants || []).filter(variant => inventoryLimit(variant) > 0 && inkUnits(variant) > 0);
      const override = answers.inkOverrides?.[months];
      let inkLines;
      if (override) {
        inkLines = [];
        for (const [id, quantity] of Object.entries(override)) {
          const variant = candidates.find(variant => String(variant.id) === id);
          const allocation = planName && variant?.plans?.find(plan => plan.name === planName);
          if (!Number.isInteger(quantity) || quantity < 0 || quantity > (variant ? inventoryLimit(variant) : 0) || (quantity > 0 && (!variant || (planName && !allocation)))) {
            return { model, stockIssue: true, error: 'The selected ink pack quantity is unavailable for this delivery. Adjust the ink quantity or purchase option.' };
          }
          if (variant) inkChoices.push({ product: model.ink, variant, quantity, unitPrice: allocation ? allocation.price : variant.price, allocation, kind: 'ink' });
        }
        inkLines = inkChoices.filter(line => line.quantity > 0);
      } else {
        const estimate = includedInkers ? Math.max(1, inkersNeeded - includedInkers) : inkersNeeded;
        const plan = planPacks(candidates, estimate, planName, inkUnits);
        if (!plan) return { model, stockIssue: true, error: `Not enough compatible ink is available for this ${months}-month delivery. Adjust the purchase option or remove extra ink.` };
        inkLines = plan.lines.map(line => ({ ...line, product: model.ink, kind: 'ink' }));
        inkChoices.push(...inkLines);
      }
      for (const line of inkChoices) {
        line.requestedQuantity = line.quantity;
        if (answers.excludedInkVariants?.map(String).includes(String(line.variant.id))) line.quantity = 0;
      }
      lines.push(...inkLines.filter(line => line.quantity > 0));
    }
    const suppliedSleeves = colorPlans.reduce((total, plan) => total + plan.supplied, 0);
    const suppliedInkers = includedInkers + lines.filter(line => line.kind === 'ink').reduce((sum, line) => sum + inkUnits(line.variant) * line.quantity, 0);
    return { model, gun, lines, labelsNeeded, sleevesNeeded, colorPlans, selectedStyles: wantsLabels ? styles : [], suppliedSleeves,
      includedInkers, inkersNeeded, suppliedInkers, inkChoices,
      incompleteLabels: colorPlans.some(plan => plan.supplied * model.labelsPerSleeve < plan.needed),
      incompleteInk: (answers.supplies !== 'labels' && wantsSupplies && model.key !== 'monarch-1153') && suppliedInkers < inkersNeeded,
      suppliedLabels: suppliedSleeves * model.labelsPerSleeve,
      total: lines.reduce((total, line) => total + line.unitPrice * line.quantity, 0) };
  }

  function subscriptionPlans(model, answers) {
    if (!model || !(answers.owner === 'yes' || answers.extras === 'yes')) return [];
    const groups = [];
    if (answers.supplies !== 'ink') {
      const selected = selectedStyles(answers.style);
      const mix = colorMix(selected, answers.colorMix);
      const styles = selected.filter(style => mix[style] > 0 && !answers.excludedColors?.includes(style));
      groups.push(...styles.map(style => labelVariants(model.labels, style)));
    }
    if (answers.supplies !== 'labels' && answers.includeInk !== false && model.key !== 'monarch-1153') groups.push((model.ink?.variants || []).filter(variant => inventoryLimit(variant) > 0));
    if (!groups.length) return [];
    const months = Number(answers.months || 3);
    const plansByStyle = groups.map(variants => new Map(variants.flatMap(variant => variant.plans || []).filter(plan => {
      const match = plan.name.match(/(\d+)\s*month/i);
      return match && Number(match[1]) === months;
    }).map(plan => [plan.name, plan])));
    return [...plansByStyle[0].values()].filter(plan => plansByStyle.every(plans => plans.has(plan.name)));
  }

  function subscriptionOffers(models, answers) {
    const model = chooseModel(models, answers);
    if (!(answers.owner === 'yes' || answers.extras === 'yes')) return [];
    return [6, 3].map(months => {
      const candidates = subscriptionPlans(model, { ...answers, months }).map(plan => {
        const result = recommendation(models, { ...answers, months }, plan.name);
        if (result.error) return null;
        const recurring = result.lines.filter(line => line.allocation);
        const recurringTotal = recurring.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);
        const regularRecurringTotal = recurring.reduce((sum, line) => sum + line.variant.price * line.quantity, 0);
        return { months, plan, result, recurringTotal, regularRecurringTotal,
          savingsPercent: regularRecurringTotal > recurringTotal ? Math.round((regularRecurringTotal - recurringTotal) * 100 / regularRecurringTotal) : 0 };
      }).filter(Boolean).sort((a, b) => a.result.total - b.result.total);
      return candidates[0] || { months, plan: null };
    });
  }

  root.StoreSuppliesFinder = { stepsFor, monthlyProducts, labelsForPeriod, chooseModel, packUnits, inkUnits, inventoryLimit, addedCartLines, colorMix, adjustColorMix, variantStyle, styleOptions, selectedStyles, labelVariants, planPacks, recommendation, subscriptionPlans, subscriptionOffers };
})(globalThis);
